import { useEffect, useRef, useState } from "react";
import styles from "../styles/runner.module.css";

interface Props {
  passingScore: number;
  props: {
    starting_moisture?: number;
    starting_aeration?: number;
    duration_seconds?: number;
    target_moisture_range?: [number, number];
    target_aeration_range?: [number, number];
  };
  onScored: (result: {
    score: number;
    passed: boolean;
    raw: {
      moisture_samples: number[];
      aeration_samples: number[];
      in_range_ms: number;
    };
  }) => void;
}

/**
 * Hero mission: simulador de composta.
 *
 * El learner ajusta dos sliders (humedad y aireación) en tiempo real durante
 * `duration_seconds`. Si los valores se mantienen dentro de los rangos
 * objetivo, se acumula `in_range_ms`. Al final se calcula la fracción del
 * tiempo dentro del rango → score 0..1.
 *
 * El canvas renderiza capas de composta (tono que cambia según humedad +
 * agujeros de aire que crecen con aireación). No es lindo — pero prueba que
 * podemos meter render imperativo Canvas dentro del mismo runner que las
 * primitivas declarativas.
 */
export function CompostSimulator({ passingScore, props, onScored }: Props) {
  const duration = props.duration_seconds ?? 10;
  const targetMoisture = props.target_moisture_range ?? [0.5, 0.7];
  const targetAeration = props.target_aeration_range ?? [0.5, 0.8];

  const [moisture, setMoisture] = useState(props.starting_moisture ?? 0.4);
  const [aeration, setAeration] = useState(props.starting_aeration ?? 0.3);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const inRangeMsRef = useRef(0);
  const moistureSamplesRef = useRef<number[]>([]);
  const aerationSamplesRef = useRef<number[]>([]);
  const startTsRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Render loop.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;

    let rafId: number | null = null;
    const draw = () => {
      const w = c.width;
      const h = c.height;
      ctx.clearRect(0, 0, w, h);
      // background
      ctx.fillStyle = "#fff8e7";
      ctx.fillRect(0, 0, w, h);
      // soil layer (color shifts brown -> dark mud as moisture rises)
      const r = Math.round(120 - moisture * 60);
      const g = Math.round(80 - moisture * 40);
      const b = Math.round(40 - moisture * 20);
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.fillRect(0, h * 0.25, w, h * 0.75);
      // aeration "holes"
      const holeCount = Math.round(4 + aeration * 20);
      ctx.fillStyle = "rgba(255, 248, 231, 0.85)";
      for (let i = 0; i < holeCount; i++) {
        const x = ((i * 53) % w) + 12;
        const y = h * 0.4 + ((i * 31) % (h * 0.5));
        const radius = 2 + aeration * 8;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      // status badge
      const inMoist =
        moisture >= targetMoisture[0] && moisture <= targetMoisture[1];
      const inAerate =
        aeration >= targetAeration[0] && aeration <= targetAeration[1];
      ctx.fillStyle = inMoist && inAerate ? "#06a77d" : "#ef4444";
      ctx.beginPath();
      ctx.arc(w - 22, 22, 10, 0, Math.PI * 2);
      ctx.fill();
    };

    const loop = () => {
      draw();
      rafId = requestAnimationFrame(loop);
    };
    loop();
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [moisture, aeration, targetMoisture, targetAeration]);

  // Timer loop.
  useEffect(() => {
    if (!running) return;
    startTsRef.current = performance.now();
    inRangeMsRef.current = 0;
    moistureSamplesRef.current = [];
    aerationSamplesRef.current = [];

    let lastTs = performance.now();
    let lastSampleTs = performance.now();
    let id: number | null = null;

    const tick = () => {
      const now = performance.now();
      const dt = now - lastTs;
      lastTs = now;
      const elapsedMs = now - (startTsRef.current ?? now);
      setElapsed(elapsedMs / 1000);

      const inMoist =
        moisture >= targetMoisture[0] && moisture <= targetMoisture[1];
      const inAerate =
        aeration >= targetAeration[0] && aeration <= targetAeration[1];
      if (inMoist && inAerate) inRangeMsRef.current += dt;

      if (now - lastSampleTs > 250) {
        moistureSamplesRef.current.push(Number(moisture.toFixed(3)));
        aerationSamplesRef.current.push(Number(aeration.toFixed(3)));
        lastSampleTs = now;
      }

      if (elapsedMs >= duration * 1000) {
        const total = duration * 1000;
        const fraction = total === 0 ? 0 : inRangeMsRef.current / total;
        const score = Math.max(0, Math.min(1, fraction));
        const passed = score >= passingScore;
        setRunning(false);
        setSubmitted(true);
        onScored({
          score,
          passed,
          raw: {
            moisture_samples: moistureSamplesRef.current,
            aeration_samples: aerationSamplesRef.current,
            in_range_ms: Math.round(inRangeMsRef.current),
          },
        });
        return;
      }
      id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => {
      if (id !== null) cancelAnimationFrame(id);
    };
    // We intentionally close over the current moisture/aeration via state;
    // updates re-trigger the effect through dependency on the snapshots.
  }, [running, moisture, aeration, duration, passingScore, targetMoisture, targetAeration, onScored]);

  return (
    <div className={styles.col}>
      <h2 className={styles.prompt}>
        Mantén la composta sana durante {duration} segundos
      </h2>
      <p style={{ color: "#4b5563", margin: 0, fontSize: 14 }}>
        Humedad objetivo: {Math.round(targetMoisture[0] * 100)}–
        {Math.round(targetMoisture[1] * 100)}%. Aireación objetivo:{" "}
        {Math.round(targetAeration[0] * 100)}–
        {Math.round(targetAeration[1] * 100)}%.
      </p>

      <div className={styles.canvasWrap}>
        <canvas
          ref={canvasRef}
          width={320}
          height={220}
          style={{ width: "100%", maxWidth: 320, borderRadius: 12 }}
        />
      </div>

      <div className={styles.col}>
        <div className={styles.sliderLabel}>
          <span>Humedad</span>
          <span>{Math.round(moisture * 100)}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(moisture * 100)}
          onChange={(e) => setMoisture(Number(e.target.value) / 100)}
          disabled={submitted}
        />
      </div>
      <div className={styles.col}>
        <div className={styles.sliderLabel}>
          <span>Aireación</span>
          <span>{Math.round(aeration * 100)}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(aeration * 100)}
          onChange={(e) => setAeration(Number(e.target.value) / 100)}
          disabled={submitted}
        />
      </div>

      {!running && !submitted && (
        <button
          type="button"
          className={styles.primaryButton}
          onClick={() => setRunning(true)}
        >
          Iniciar simulación
        </button>
      )}
      {running && (
        <div className={styles.summary}>
          <strong>En curso…</strong>
          <span>
            {elapsed.toFixed(1)}s / {duration}s. Mantén la composta en verde.
          </span>
        </div>
      )}
      {submitted && (
        <div className={styles.summary}>
          <strong>Simulación terminada</strong>
          <span>
            Tiempo en rango: {(inRangeMsRef.current / 1000).toFixed(1)}s de {duration}s.
          </span>
        </div>
      )}
    </div>
  );
}
